import { useTranslation } from 'react-i18next';
import { useRef } from 'react';
import { useInViewport } from 'react-in-viewport';
import STRING_KEYS from '../../../../../language/keys';
import { convertMonthToFloatYears } from '../../../../../utils';
import { TenureChart } from './tenure-chart/tenure-chart.component';
import {
  Container,
  Label,
  MainContainer,
  Title,
  Years,
  MarkedText,
  Description,
} from './tenure-time-widget.styled';

type TenureTimeWidgetProps = {
  talentName: string,
  talentValue: number,
  companyValue: number,
  talentProfileImage: string,
  companyProfileImage: string,
  companyName: string,
}

export const TenureTimeWidget = ({
  talentName,
  talentValue,
  talentProfileImage,
  companyProfileImage,
  companyValue,
  companyName,
}:TenureTimeWidgetProps)
: JSX.Element => {
  const containerRef = useRef(null);
  const {
    inViewport,
    enterCount,
  } = useInViewport(
    containerRef,
    {},
    {},
  );
  const { t: translate } = useTranslation();

  const computePercent = () => {
    if (companyValue < talentValue) {
      return `${translate(STRING_KEYS.TENURE_TIME_LONGER, {
        value: Math.floor(100 - (companyValue / talentValue) * 100),
      })}`;
    }

    return `${translate(STRING_KEYS.TENURE_TIME_SHORTER, {
      value: Math.floor(100 - (talentValue / companyValue) * 100),
    })}`;
  };

  const isTalentExperienceLessThanYear = talentValue < 12;
  const isTalentExperienceDividedByYear = talentValue % 12 === 0;

  const getYearsString = () => {
    const years = convertMonthToFloatYears(talentValue);
    if (years === 1) {
      return translate(STRING_KEYS.INDUSTRY_YEAR);
    }

    return translate(STRING_KEYS.INDUSTRY_YEARS);
  };

  const getMonthsString = () => {
    if (talentValue === 1) {
      return translate(STRING_KEYS.INDUSTRY_MONTH);
    }

    return translate(STRING_KEYS.INDUSTRY_MONTHS);
  };

  return  (
    <MainContainer ref={ containerRef }>
      <Container>
        <Label>{translate(STRING_KEYS.CURRENT_TENURE_TIME)}</Label>
        <Title>
          <Years
            end={ isTalentExperienceLessThanYear ?
              talentValue : convertMonthToFloatYears(talentValue) }
            duration={ inViewport && enterCount === 1 ? 1 :  0 }
            decimal="."
            decimals={ isTalentExperienceLessThanYear || isTalentExperienceDividedByYear ? 0 : 1 }
            useEasing
            redraw
          />
          {isTalentExperienceLessThanYear ?
            getMonthsString() : getYearsString()}
        </Title>
        {companyValue && talentValue && (
          <Description>
            <MarkedText>{computePercent()}</MarkedText>
            {translate(STRING_KEYS.THAN_MOST, { companyName })}
          </Description>
        )}
      </Container>
      <TenureChart
        talentName={ talentName }
        talentValue={ talentValue }
        talentProfileImage={ talentProfileImage }
        companyProfileImage={ companyProfileImage }
        companyValue={ companyValue }
        companyName={ companyName }
        isEnableAnimation={ inViewport && enterCount === 1 }
      />
    </MainContainer>
  );
};
